import 'lazysizes/plugins/rias/ls.rias';
import lazySizes from 'lazysizes';
import { debounceTime, fromEvent } from 'rxjs';
import setUrlParams from 'utils/set-url-params';

type Target = {
  mediaQuery: string;
  aspect: number;
  element: HTMLElement;
  width: number;
  height: number;
};

type Crop = {
  top: number;
  left: number;
  width: number;
  height: number;
};

lazySizes.cfg.loadHidden = false;

const getTargets = (event: CustomEvent): Target[] => {
  if (!(event.target instanceof HTMLElement)) {
    throw new Error('Event target must be instance of HTMLElement');
  }

  const targets = [
    {
      mediaQuery: '', // Empty media query
      aspect: event.detail.aspectratio,
      element: event.target,
      width: parseInt(event.target.dataset.maxWidth, 10),
      height: parseInt(event.target.dataset.maxHeight, 10),
    },
  ];

  if (event.detail.isPicture) {
    const parent = event.target.parentElement;
    const sources = parent.querySelectorAll('source');

    sources.forEach((source) => {
      targets.push({
        mediaQuery: source.media,
        aspect: parseFloat(source.dataset.aspectratio) || parent.clientWidth / parent.clientHeight,
        element: source,
        width: parseInt(source.dataset.maxWidth, 10),
        height: parseInt(source.dataset.maxHeight, 10),
      });
    });
  }

  return targets;
};

const getWidths = (target: Target): number[] => {
  const sizes = [40, 80, 160, 320, 480, 640, 768, 1024, 1280, 1536, 2048, 2560, 3072];

  return sizes.filter((size) => {
    if (target.aspect) {
      if (size <= target.width && size / target.aspect <= target.height) {
        return true;
      }
    } else if (size <= target.width) {
      return true;
    }

    return false;
  });
};

const getCrop = (target: Target): Crop => {
  const focal = {
    x: Math.round((target.width * parseFloat(target.element.dataset.focalX)) / 100),
    y: Math.round((target.height * parseFloat(target.element.dataset.focalY)) / 100),
  };

  const crop = {
    top: 0,
    left: 0,
    width: target.width,
    height: target.height,
  };

  const sourceAspectRatio = target.width / target.height;

  if (target.aspect > sourceAspectRatio) {
    const height = Math.round(target.width / target.aspect);
    const top = Math.round(focal.y - height / 2);

    if (top + height > target.height) {
      crop.top = target.height - height;
    } else {
      crop.top = top;
    }

    crop.height = height;
  } else if (target.aspect < sourceAspectRatio) {
    const width = Math.round(target.height * target.aspect);
    const left = Math.round(focal.x - width / 2);

    if (left + width > target.width) {
      crop.left = target.width - width;
    } else {
      crop.left = left;
    }

    crop.width = width;
  }

  return crop;
};

const setParams = (element: HTMLElement) => {
  let { src } = element.dataset;

  if (!src) {
    return;
  }

  if (src.startsWith('//')) {
    src = `https:${src}`;
  }

  const url = new URL(src, window.location.origin);
  setUrlParams(url, {
    crop: 'region',
    crop_height: '{cropheight}',
    crop_left: '{cropleft}',
    crop_top: '{croptop}',
    crop_width: '{cropwidth}',
    height: '{height}',
    width: '{width}',
  });
  url.searchParams.sort();

  element.dataset.src = decodeURI(url.toString());
};

document.addEventListener('lazyriasmodifyoptions', (event: CustomEvent) => {
  if (!(event.target instanceof HTMLElement)) {
    return;
  }

  if (!event.detail.aspectratio) {
    const parent = event.target.parentElement;

    event.detail.aspectratio = parent.clientWidth / parent.clientHeight;
  }

  const targets = getTargets(event);
  const currentTarget = targets
    .reverse()
    .find((target) => window.matchMedia(target.mediaQuery).matches);

  event.detail.widths = getWidths(currentTarget);

  const crop = getCrop(currentTarget);

  event.detail.cropwidth = crop.width;
  event.detail.cropheight = crop.height;
  event.detail.croptop = crop.top;
  event.detail.cropleft = crop.left;
});

let currentWidth = window.innerWidth;
const reloadImages = () => {
  if (currentWidth === window.innerWidth) {
    return;
  }

  currentWidth = window.innerWidth;
  const images = document.querySelectorAll(`img.${lazySizes.cfg.loadedClass}`);

  images.forEach((image) => {
    image.classList.remove(lazySizes.cfg.loadedClass);
    image.classList.add(lazySizes.cfg.lazyClass);
  });
};

const handleSetParams = (image: HTMLElement) => {
  setParams(image);

  if (image.parentNode && /^picture$/i.test(image.parentNode.nodeName || '')) {
    image.parentNode.querySelectorAll('source').forEach((source) => {
      setParams(source);
    });
  }
};

document
  .querySelectorAll<HTMLElement>(`.${lazySizes.cfg.lazyClass ?? 'lazyload'}`)
  .forEach((image) => {
    handleSetParams(image);
  });

new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'childList' && mutation.addedNodes.length) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }

        if (node instanceof HTMLImageElement) {
          handleSetParams(node);
        } else {
          node
            .querySelectorAll<HTMLElement>(`.${lazySizes.cfg.lazyClass ?? 'lazyload'}`)
            .forEach((image) => {
              handleSetParams(image);
            });
        }
      });
    }
  });
}).observe(document.documentElement, {
  childList: true,
  subtree: true,
});

fromEvent(window, 'resize').pipe(debounceTime(250)).subscribe(reloadImages);
